import React, { useCallback, useMemo } from 'react';
import { createEditor } from 'slate';
import { Slate, Editable, withReact } from 'slate-react';
import { withHistory } from 'slate-history';
import forItemContent from '../item-content/item-content-plugin';
import {
  ActionsContext,
  CurrentItemContext,
  DataContext,
  TimestampContext
} from '../item-content/polling-context';
import { getFontFamilyStyle } from '../../utils/font-family';

/**
 * Renders a Slate document for a questionnaire item, providing
 * the same context values that existing element renderers expect
 * (useData, useDataActions, etc.) without a full PollingContext.
 */
const QuestionnaireItemContent = ({ document, responses, onUpdateData }) => {
  const editor = useMemo(() => {
    const e = forItemContent(withHistory(withReact(createEditor())));
    e.data = responses || {};
    e.readOnly = true;
    e.interactionEnabled = true;
    e.sendData = onUpdateData || (() => {});
    return e;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep editor.data in sync with responses
  useMemo(() => {
    editor.data = responses || {};
  }, [editor, responses]);

  const updateData = useCallback(
    (newData) => {
      if (onUpdateData) onUpdateData(newData);
    },
    [onUpdateData]
  );

  const actions = useMemo(
    () => ({
      updateData,
      resetData: () => {},
      setCurrentItem: () => {},
      setSessionID: () => {}
    }),
    [updateData]
  );

  const timestamps = useMemo(
    () => ({
      currentItemTimestamp: Date.now(),
      dataTimestamp: Date.now()
    }),
    []
  );

  const defaultValue = [
    {
      type: 'paragraph',
      children: [{ text: '', fontFamily: 'Roboto', fontSize: 20 }]
    }
  ];

  const slateValue = document && document.length > 0 ? document : defaultValue;

  return (
    <ActionsContext.Provider value={actions}>
      <DataContext.Provider value={responses || {}}>
        <CurrentItemContext.Provider value={undefined}>
          <TimestampContext.Provider value={timestamps}>
            <Slate editor={editor} initialValue={slateValue} onChange={() => {}}>
              <Editable
                readOnly
                renderElement={useCallback(
                  (props) => (
                    <RenderElement {...props} />
                  ),
                  []
                )}
                renderLeaf={useCallback(
                  (props) => (
                    <RenderLeaf {...props} />
                  ),
                  []
                )}
                style={{ minHeight: '40px' }}
              />
            </Slate>
          </TimestampContext.Provider>
        </CurrentItemContext.Provider>
      </DataContext.Provider>
    </ActionsContext.Provider>
  );
};

// Simple element renderer that handles the common types
// For full fidelity, this delegates to ItemContent's renderer patterns
const RenderElement = (props) => {
  const { attributes, children, element } = props;

  switch (element.type) {
    case 'paragraph':
    default:
      return (
        <div
          {...attributes}
          style={{
            lineHeight: element.lineHeight || 1.2,
            marginBottom: '0.5vmin',
            textAlign: element.textAlign
          }}
        >
          {children}
        </div>
      );
  }
};

const RenderLeaf = ({ attributes, children, leaf }) => {
  return (
    <span
      {...attributes}
      style={{
        backgroundColor: leaf.backgroundColor || '',
        color: leaf.color || '',
        fontFamily: getFontFamilyStyle(leaf.fontFamily),
        fontSize: leaf.fontSize ? `${leaf.fontSize * 10}%` : '',
        fontStyle: leaf.italic ? 'italic' : 'normal',
        fontWeight: leaf.bold ? 'bold' : 'normal',
        textDecoration: `${leaf.strikethrough ? 'line-through ' : ''}${leaf.underline ? 'underline' : ''}`
      }}
    >
      {children}
    </span>
  );
};

export default QuestionnaireItemContent;
